// Stage 7 — keeps the #help-shortcuts tab in sync with the shortcut registry.
//
// The help panel's tab-switching machinery (events.ts) already toggles the
// hidden attribute when the user clicks a tab; this module just owns the
// content of the "Shortcuts" panel, regenerating it whenever commands are
// added/removed/rebound.

import { getShortcutRegistry, formatChord, type CommandDef } from '@input/shortcutRegistry';

const ROOT_ID = 'help-shortcuts-list';

function groupByCategory(defs: readonly CommandDef[]): Map<CommandDef['category'], CommandDef[]> {
    const groups = new Map<CommandDef['category'], CommandDef[]>();
    for (const def of defs) {
        let g = groups.get(def.category);
        if (!g) { g = []; groups.set(def.category, g); }
        g.push(def);
    }
    for (const g of groups.values()) g.sort((a, b) => a.label.localeCompare(b.label));
    return groups;
}

const CATEGORY_ORDER: ReadonlyArray<CommandDef['category']> = [
    'Palette', 'Navigation', 'Primitives', 'Selections', 'Reductions', 'View', 'Theme', 'Misc',
];

export function installShortcutsHelpTab(): () => void {
    const registry = getShortcutRegistry();

    const render = (): void => {
        const root = document.getElementById(ROOT_ID);
        if (!root) return;
        root.textContent = '';
        const groups = groupByCategory(registry.list());

        const cats = CATEGORY_ORDER.filter((c) => groups.has(c));
        for (const cat of cats) {
            const defs = groups.get(cat);
            if (!defs?.length) continue;
            const section = document.createElement('div');
            section.className = 'help-shortcuts-group';
            const h5 = document.createElement('h5');
            h5.textContent = cat;
            section.appendChild(h5);

            const list = document.createElement('ul');
            for (const def of defs) {
                const item = document.createElement('li');
                if (def.enabled === false) item.classList.add('is-disabled');
                const label = document.createElement('span');
                label.className = 'help-shortcuts-label';
                label.textContent = def.label;
                item.appendChild(label);
                const keys = registry.keysFor(def.id);
                if (keys.length > 0) {
                    const kbd = document.createElement('kbd');
                    kbd.className = 'help-shortcuts-keys';
                    kbd.textContent = formatChord(keys[0]!);
                    item.appendChild(kbd);
                }
                list.appendChild(item);
            }
            section.appendChild(list);
            root.appendChild(section);
        }

        if (cats.length === 0) {
            const empty = document.createElement('p');
            empty.className = 'help-shortcuts-empty';
            empty.textContent = 'No commands registered yet.';
            root.appendChild(empty);
        }
    };

    render();
    const off = registry.subscribe(render);
    return off;
}
